import { YieldFetcher } from '../../types'

/**
 * Ankr Liquid Staking metrics. A single endpoint returns the current
 * yield for every staking service. The `apy` field is a simple,
 * non-compounded annualization in percent (rewards over the last 7 days
 * / staked amount * 5200), so it is used directly as an APR percent.
 */
const ANKR_METRICS_URL = 'https://api.staking.ankr.com/v1alpha/metrics'

interface AnkrAsset {
  /** Ankr `serviceName` in the metrics response */
  serviceName: string
  /** intrinsic-yield key — the token's `assetGroup` in the token lists */
  assetGroup: string
  /** reward-bearing token symbol */
  symbol: string
  /** deployed token addresses keyed by chain id */
  addresses: Record<number, string>
}

/**
 * Reward-bearing Ankr LSTs, pairing each metrics `serviceName` with the
 * `assetGroup` the token lists use so the yield attaches to the right
 * token on every chain it is deployed on.
 */
const ANKR_ASSETS: AnkrAsset[] = [
  {
    serviceName: 'eth',
    assetGroup: 'ANKRETH',
    symbol: 'ankrETH',
    addresses: {
      1: '0xE95A203B1a91a908F9B9CE46459d101078c2c3cb',
      56: '0xe05A08226c49b636ACf99c40Da8DC6aF83CE5bB3',
      137: '0xC4e82BA0Fe6763cbE5E9CbCA0ba7cbD6F91C6018',
      250: '0x12D8CE035c5DE3Ce39B1fDD4C1d5a745EAbA3b8C',
      43114: '0x12D8CE035c5DE3Ce39B1fDD4C1d5a745EAbA3b8C',
    },
  },
  {
    serviceName: 'polygon',
    assetGroup: 'ANKRMATIC',
    symbol: 'ankrMATIC',
    addresses: {
      1: '0x26dcFbFa8Bc267b250432c01C982Eaf81cC5480C',
      56: '0x738d96Caf7096659DB4C1aFbf1E1BDFD281f388C',
      137: '0x0E9b89007eEE9c958c0EDA24eF70723C2C93dD58',
    },
  },
  {
    serviceName: 'bnb',
    assetGroup: 'ANKRBNB',
    symbol: 'ankrBNB',
    addresses: {
      56: '0x52F24a5e03aee338Da5fd9Df68D2b6FAe1178827',
    },
  },
  {
    serviceName: 'avax',
    assetGroup: 'ANKRAVAX',
    symbol: 'ankrAVAX',
    addresses: {
      43114: '0xc3344870d52688874b06d844E0C36cc39FC727F6',
    },
  },
  // Fantom (ankrFTM) is deprecated — not registered.
]

/** Ankr serviceName → intrinsic-yield asset group key */
const ANKR_SERVICE_KEYS: Record<string, string> = Object.fromEntries(
  ANKR_ASSETS.map((a) => [a.serviceName, a.assetGroup]),
)

export const ankrFetcher: YieldFetcher = {
  label: 'ANKR',
  fetch: async () => {
    const res = await fetch(ANKR_METRICS_URL).then((r) => r.json())
    const services: any[] = res?.services ?? []

    const result: Record<string, number> = {}
    for (const service of services) {
      const key = ANKR_SERVICE_KEYS[service?.serviceName]
      if (!key) continue
      const apr = Number(service?.apy)
      if (apr > 0) result[key] = apr
    }
    return result
  },
}
